/**
 * KitReviewRenderer — the bespoke review surface for the `kit-inference` operation
 * (Kit Inference slice 5, ADR-070). Registered into the per-operationType renderer
 * registry (slice 4) so the review page dispatches to it for kit runs; every other
 * operation keeps the flat `DiffReviewGrid`.
 *
 * A kit's proposed BOM is HIERARCHICAL — each ChangeItem carries
 * `cells.components.new = { bom, proposals }`. This renderer surfaces CONFIDENCE,
 * which is the whole point of the gate-less AI seam:
 *
 *  - Fail-closed on ambiguity (gate 3): a component on a thin match margin is
 *    `needs-review` and rendered DEFAULT-UNCHECKED. It is already EXCLUDED from the
 *    server-computed `bom`, so approving the run (approve-all) applies only the
 *    confident components — a needs-review line can never be wrong-applied.
 *  - An exact GTIN hit is auto-confident (badge shows the deterministic reason).
 *  - Discriminating-attribute surfacing: each proposal lists its ranked candidates
 *    (sku / gtin / brand / model) so the reviewer sees exactly what they disagree on.
 *  - A `skipped` ChangeItem (no-match / all-needs-review) shows its note NAMING the
 *    unmatched components — the no-op turned into a to-do list (gate 4).
 */

import type { ReactElement } from 'react';
import { Badge, Checkbox, Group, Stack, Table, Text } from '@mantine/core';
import type { ChangeItemType } from '@bett3r-dev/teselly-domain';
import {
  registerOperationReviewRenderer,
  type OperationReviewRendererProps
} from './OperationReview';

type ComponentProposal = {
  name: string;
  brand?: string;
  model?: string;
  qty: number;
  matchedItemId?: string;
  confidence: 'confident' | 'needs-review' | 'unmatched';
  needsReview: boolean;
  matchReason?: 'gtin-exact' | 'fuzzy-margin' | 'single-candidate';
  candidates?: Array<{ itemId: string; sku?: string; gtin?: string; brand?: string; model?: string; score: number }>;
};

type KitComponentsCell = {
  bom: Record<string, { quantity: number }>;
  proposals: ComponentProposal[];
};

const readComponentsCell = ( item: ChangeItemType ): KitComponentsCell | undefined => {
  const cell = ( item.cells as Record<string, { new?: unknown }> | undefined )?.components?.new;
  if ( cell && typeof cell === 'object' && Array.isArray(( cell as KitComponentsCell ).proposals )) {
    return cell as KitComponentsCell;
  }
  return undefined;
};

const confidenceColor = ( confidence: ComponentProposal['confidence']): string => {
  if ( confidence === 'confident' ) return 'green';
  if ( confidence === 'needs-review' ) return 'yellow';
  return 'red';
};

const ComponentRow = ({ proposal }: { proposal: ComponentProposal }): ReactElement => {
  const topCandidate = proposal.candidates?.[ 0 ];
  return (
    <Table.Tr>
      <Table.Td>
        {/* DEFAULT-UNCHECKED for needs-review / unmatched (gate 3, fail-closed). A
            confident component is already in the applied BOM. */}
        <Checkbox
          checked={proposal.confidence === 'confident'}
          readOnly
          aria-label={`component-${proposal.name}`}
        />
      </Table.Td>
      <Table.Td>
        <Text fz="sm" fw={500}>{proposal.name}</Text>
        <Text fz="xs" c="dimmed">
          {[ proposal.brand, proposal.model ].filter( Boolean ).join( ' · ' )} × {proposal.qty}
        </Text>
      </Table.Td>
      <Table.Td>
        <Group gap="xs">
          <Badge size="sm" color={confidenceColor( proposal.confidence )} variant="light">
            {proposal.confidence}
          </Badge>
          {proposal.matchReason === 'gtin-exact' && (
            <Badge size="sm" color="green" variant="outline">GTIN</Badge>
          )}
        </Group>
      </Table.Td>
      <Table.Td>
        {topCandidate ? (
          <Text fz="xs" c="dimmed">
            {[ topCandidate.sku, topCandidate.gtin, topCandidate.brand, topCandidate.model ]
              .filter( Boolean )
              .join( ' · ' )}
          </Text>
        ) : (
          <Text fz="xs" c="red">sin coincidencia</Text>
        )}
      </Table.Td>
    </Table.Tr>
  );
};

const KitReviewRenderer = ({
  changeItems,
  approvedItemIds,
  onToggleRow,
  onToggleAll
}: OperationReviewRendererProps ): ReactElement => {
  const approved = new Set( approvedItemIds );
  const actionable = changeItems.filter(( i ) => i.status !== 'skipped' && i.status !== 'rejected' );
  const allApproved = actionable.length > 0 && actionable.every(( i ) => approved.has( i.targetId ));

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Checkbox
          label="Aprobar todos"
          checked={allApproved}
          onChange={( e ) => onToggleAll( e.currentTarget.checked )}
        />
      </Group>

      {changeItems.map(( item ) => {
        const cell = readComponentsCell( item );
        const isSkipped = item.status === 'skipped' || item.status === 'rejected';
        return (
          <Stack key={item.targetId} gap="xs">
            <Group justify="space-between">
              <Group gap="xs">
                {!isSkipped && (
                  <Checkbox
                    checked={approved.has( item.targetId )}
                    onChange={( e ) => onToggleRow( item.targetId, e.currentTarget.checked )}
                    aria-label={`kit-${item.targetId}`}
                  />
                )}
                <Text fz="sm" fw={600}>{item.targetId}</Text>
                <Badge size="sm" color={isSkipped ? 'gray' : 'blue'} variant="light">
                  {item.status}
                </Badge>
              </Group>
            </Group>

            {item.note && (
              <Text fz="xs" c="dimmed">{item.note}</Text>
            )}

            {cell && cell.proposals.length > 0 && (
              <Table highlightOnHover verticalSpacing="xs" horizontalSpacing="md">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th />
                    <Table.Th>Componente</Table.Th>
                    <Table.Th>Confianza</Table.Th>
                    <Table.Th>Coincidencia</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {cell.proposals.map(( proposal, index ) => (
                    <ComponentRow key={`${item.targetId}-${index}`} proposal={proposal} />
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
};

// Register into the per-operationType renderer registry (slice 4). Additive: this
// module is imported by the operations domain barrel so the registration runs once
// at app load; anything unregistered still falls back to DiffReviewGrid.
registerOperationReviewRenderer( 'kit-inference', KitReviewRenderer );

export default KitReviewRenderer;
